import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Bell, Save, ToggleLeft, ToggleRight } from "lucide-react";

export default function Notice() {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState("");
  const [meta, setMeta] = useState({ updated_by: "", updated_at: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/notice");
      setActive(!!data?.active);
      setMessage(data?.message || "");
      setMeta({ updated_by: data?.updated_by || "", updated_at: data?.updated_at || "" });
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/notice", { active, message });
      setMeta({ updated_by: data.updated_by, updated_at: data.updated_at });
      toast.success(active ? "Notice is LIVE — staff will see it on their next login" : "Notice turned OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="notice-admin-page">
      <div className="bg-white border-l-4 border-red-500 border border-slate-200 rounded-md p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <Bell className="h-5 w-5 text-red-600" />
          </span>
          <div className="flex-1">
            <div className="font-display font-bold text-slate-900 tracking-tight text-lg">Important Notice for Staff / Branch</div>
            <div className="text-xs text-slate-500 mt-0.5">
              This message pops up on every non-admin user's first page after login. Toggle it OFF to stop showing it.
            </div>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <button
            onClick={() => setActive((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-semibold ${
              active
                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                : "bg-slate-50 text-slate-600 border-slate-200"
            }`}
            data-testid="notice-toggle"
          >
            {active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
            {active ? "Notice is ON — showing to staff & branch" : "Notice is OFF — not shown"}
          </button>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Notice Message</Label>
            <Textarea
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type the important notice here… e.g. 'Please collect Aadhaar photocopy for all PAN applications from today.'"
              className="text-sm leading-relaxed"
              data-testid="notice-message-input"
            />
            <div className="text-[11px] text-slate-400">Line breaks are preserved. This shows in a modal popup.</div>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
            <div className="text-[11px] text-slate-500">
              {meta.updated_at
                ? <>Last updated by <span className="font-semibold text-slate-700">{meta.updated_by}</span> on <span className="font-mono-num">{meta.updated_at.slice(0, 16).replace("T", " ")}</span></>
                : "Never updated yet."}
            </div>
            <Button
              onClick={save}
              disabled={saving || (active && !message.trim())}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold"
              data-testid="notice-save"
            >
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Notice"}
            </Button>
          </div>
        </div>
      </div>

      {active && message.trim() && (
        <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Preview</div>
          <div className="rounded-md border border-red-200 bg-gradient-to-br from-red-50 to-white px-4 py-4 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
            {message}
          </div>
        </div>
      )}
    </div>
  );
}
